import React, { useEffect } from "react";
import styles from "./monday-item.module.css";
import { GenericListItem } from "../generic-list-item/generic-list-item";
import { SubItemsIcon } from "../icons/sub-items-icon";
import { MondayItemType } from "frontend/canvas-designer-new/monday-import-items/monday-import-items.types";
import { useDrag, XYCoord } from "react-dnd";
import * as MathUtils from "frontend/utils/math-utils";
import cn from "classnames";
import { generateDragPreview } from "./drag-preview-generator";

export interface MondayItemProps {
  item: MondayItemType;
  groupColor: string;
  searchValue?: string;
  isSelected: boolean;
  onSelect: (itemId: string, checked: boolean) => void;
  onDragEnd: (item: MondayItemType, pos: MathUtils.PointInViewport) => void;
  onSubItemsClick?: (item: MondayItemType, groupColor: string) => void;
  selectedItems?: string[];
  allItems?: MondayItemType[];
  groupColors?: Record<string, string>;
}

export const MondayItem: React.FC<MondayItemProps> = ({
  item,
  groupColor,
  searchValue,
  isSelected,
  onSelect,
  onDragEnd,
  onSubItemsClick,
  selectedItems = [],
  allItems = [],
  groupColors = {},
}) => {
  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: "import-monday-item",
    item: () => ({ item }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (_, monitor) => {
      const dropPosition = (monitor.getDropResult() as { position: XYCoord | null })?.position;
      const pos = (dropPosition ?? monitor.getClientOffset() ?? { x: 0, y: 0 }) as MathUtils.PointInViewport;

      const forbiddenZone = document.getElementById("monday-import-items-container");
      if (!forbiddenZone) {
        return;
      }
      const zoneRect = forbiddenZone.getBoundingClientRect();

      // 4. Check if the mouse coordinates are inside the zone's rectangle
      const isInside =
        pos.x >= zoneRect.left && pos.x <= zoneRect.right && pos.y >= zoneRect.top && pos.y <= zoneRect.bottom;
      if (isInside) {
        return;
      }
      onDragEnd(item, pos);
    },
  }));

  // Set custom drag preview when component mounts or when selection changes
  useEffect(() => {
    const setupDragPreview = async () => {
      if (isSelected && selectedItems.includes(item.id)) {
        const selectedItemsData = allItems.filter((itm) => selectedItems.includes(itm.id));
        if (selectedItemsData.length > 1) {
          const previewImage = await generateDragPreview(selectedItemsData, groupColors);
          preview(previewImage, { anchorX: 0.5, anchorY: 0.5 });
          return;
        }
      }
      preview(null);
    };
    setupDragPreview();
  }, [preview, isSelected, selectedItems, item.id, allItems, groupColors]);

  const rightContent = item.subItems && item.subItems.length > 0 && (
    <div
      className={cn(styles.subItemsWrapper, {
        [styles.hasSearchValue]: !!searchValue,
      })}
      onClick={(e) => {
        e.stopPropagation();
        onSubItemsClick?.(item, groupColor);
      }}
    >
      <SubItemsIcon />
      <div className={styles.subItemsCount}>{item.subItems.length}</div>
    </div>
  );

  return (
    <GenericListItem
      id={item.id}
      title={item.name}
      indicatorColor={groupColor}
      isSelected={isSelected}
      onSelect={onSelect}
      rightContent={rightContent}
      searchValue={searchValue}
      ref={drag}
      titleClassName={styles.itemTitle}
      className={cn({
        [styles.dragging]: isDragging,
      })}
    />
  );
};
